<script lang="ts">
  import type {
    FirmListQuery,
    LastContactBucket,
    LawyerCountBucket,
    NextActionBucket,
  } from '@shared/types/firm';
  import type { Firm } from '@domain/firm/model';
  import {
    nextActionAttention,
    nextActionAttentionAriaLabel,
  } from '@domain/firm/next-action-attention';
  import {
    BAR_ASSOCIATION_DISPLAY_OPTIONS,
    CUSTOMER_STATUS_OPTIONS,
    CUSTOMER_STATUS_VALUES,
    LAST_CONTACT_BUCKET_OPTIONS,
    LAWYER_COUNT_BUCKET_OPTIONS,
    NEXT_ACTION_BUCKET_OPTIONS,
    CUSTOMER_STATUS_LABELS,
    formatBarAssociationLabel,
    type SimpleOption,
  } from '@shared/constants/choices';
  import {
    ACTION_TYPE_LABELS,
    ACTION_TYPE_ORDER,
    type ActionType,
  } from '@shared/types/sales-action';
  import { formatDate } from '@shared/util/format';
  import { firmBulkSalesSummaryLabel } from '@domain/bulk-sales/labels';
  import FilterChip from '@shared/ui/FilterChip.svelte';

  type SortField = NonNullable<FirmListQuery['sort_by']>;

  interface Props {
    firms: Firm[];
    /** 一覧クエリ。 並べ替え状態と各列フィルタの現在値を読む。 */
    query: FirmListQuery;
    /** 利用ソリューションフィルタの選択肢。 ページ側で master から取得して渡す。 */
    productOptions: SimpleOption[];
    /** 担当者フィルタの選択肢 (PR-ACC-3)。 ページ側で app_accounts から取得して渡す。 */
    accountOptions: SimpleOption[];
    /** 担当者表示名の解決用。inactive な既存割当も含む。 */
    accountLabelOptions: SimpleOption[];
    /** 一括操作の選択チェックボックスを表示するか。ページの選択モード中だけ true。 */
    canSelect: boolean;
    /** 一覧再取得中など、表示中の行が最新確定状態でないときは選択を止める。 */
    selectionDisabled?: boolean;
    /** 一括操作のために選択中の法律事務所 id 集合。 */
    selectedIds: Set<string>;
    /** 空状態から検索・列フィルタをまとめて戻す導線の表示制御。 */
    hasActiveFilters: boolean;
    /** 列ヘッダーのフィルタ変更を親へ委譲する。 */
    onChange: (next: FirmListQuery) => void;
    /** 列ヘッダーの並べ替え入口。 */
    onSort: (field: SortField) => void;
    /** 検索欄も含めて一覧フィルタを初期状態へ戻す。 */
    onClearFilters: () => void;
    /** 行のチェックボックス切り替え。 */
    onToggleRow: (id: string, checked: boolean) => void;
    /** 表示中の全行の選択切り替え。 */
    onToggleAll: (checked: boolean) => void;
  }

  let {
    firms,
    query,
    productOptions,
    accountOptions,
    accountLabelOptions,
    canSelect,
    selectionDisabled = false,
    selectedIds,
    hasActiveFilters,
    onChange,
    onSort,
    onClearFilters,
    onToggleRow,
    onToggleAll,
  }: Props = $props();

  // 担当者 id → 表示ラベルの索引 (一覧セル表示用)。
  const accountLabelById = $derived(
    new Map(accountLabelOptions.map((o) => [o.value, o.label])),
  );

  const sortBy = $derived<SortField>(query.sort_by ?? 'updated_at');
  const sortDir = $derived(query.sort_dir ?? 'desc');
  const allSelected = $derived(
    firms.length > 0 && firms.every((firm) => selectedIds.has(firm.id)),
  );
  const someSelected = $derived(
    !allSelected && firms.some((firm) => selectedIds.has(firm.id)),
  );
  const colCount = $derived(canSelect ? 10 : 9);

  // ── 列ヘッダーフィルタの選択肢 (旧 FirmListFilters から移設) ──────────────

  // ステータス選択肢 = 全9値 (受注/失注を含む)。 既定 (= 未指定 or 全選択) のときは
  // チップを中立表示にし、 部分選択のときだけ選択値を見せる。
  const statusChipValue = $derived<string[]>(
    query.top_sales_grades &&
      query.top_sales_grades.length < CUSTOMER_STATUS_VALUES.length
      ? query.top_sales_grades
      : [],
  );

  const ACTION_TYPE_OPTIONS: SimpleOption[] = ACTION_TYPE_ORDER.map((t) => ({
    value: t,
    label: ACTION_TYPE_LABELS[t],
  }));

  // ── フィルタ適用 (query デルタを作って親へ委譲) ───────────────────────────

  function applyLawyerCountBuckets(values: string[]) {
    onChange({
      ...query,
      lawyer_count_buckets:
        values.length > 0 ? (values as LawyerCountBucket[]) : undefined,
      page: 1,
    });
  }

  function applyBarAssociations(values: string[]) {
    onChange({
      ...query,
      bar_associations: values.length > 0 ? values : undefined,
      page: 1,
    });
  }

  function applyCustomerStatuses(values: string[]) {
    // クリア (空)・全選択は「絞り込みなし」(全9値表示) としてフィルタを外す。
    onChange({
      ...query,
      top_sales_grades:
        values.length > 0 && values.length < CUSTOMER_STATUS_VALUES.length
          ? values
          : undefined,
      page: 1,
    });
  }

  function applyNextActionTypes(values: string[]) {
    onChange({
      ...query,
      next_action_types:
        values.length > 0 ? (values as ActionType[]) : undefined,
      page: 1,
    });
  }

  function applyProductIds(values: string[]) {
    onChange({
      ...query,
      product_ids: values.length > 0 ? values : undefined,
      page: 1,
    });
  }

  function applyAssignees(values: string[]) {
    // 担当者を指定したら「未担当のみ」(詳細条件) とは両立しないため同時に解除する。
    onChange({
      ...query,
      assignee_account_ids: values.length > 0 ? values : undefined,
      unassigned_only: undefined,
      page: 1,
    });
  }

  function assigneeLabel(firm: Firm): string {
    if (!firm.assignee_account_id) return '-';
    return (
      accountLabelById.get(firm.assignee_account_id) ?? firm.assignee_account_id
    );
  }

  function applyNextAction(value: string | null) {
    onChange({
      ...query,
      next_action_bucket: (value as NextActionBucket | null) ?? undefined,
      page: 1,
    });
  }

  function applyLastContact(value: string | null) {
    onChange({
      ...query,
      last_contact_bucket: (value as LastContactBucket | null) ?? undefined,
      page: 1,
    });
  }

  // ── セル表示ヘルパ ────────────────────────────────────────────────────────

  function indicator(field: SortField): string {
    if (field !== sortBy) return '';
    return sortDir === 'asc' ? ' ▲' : ' ▼';
  }

  // フィルタチップと同居する列の並べ替え入口。 非選択時は中立マーク (↕)、
  // 選択時は方向 (▲ / ▼) を出す。
  function sortMark(field: SortField): string {
    if (field !== sortBy) return '↕';
    return sortDir === 'asc' ? '▲' : '▼';
  }

  function lawyerCountDisplay(firm: Firm): string {
    return firm.lawyer_count != null ? String(firm.lawyer_count) : '-';
  }

  // 次アクション (catalog No.6) は sales_actions.action_type (次アクションマスタ) を表示する。
  function nextActionLabel(value: string | null | undefined): string {
    if (!value) return '-';
    return ACTION_TYPE_LABELS[value as ActionType] ?? value;
  }

  // 次アクション列の注意表示 (Issue #524 PR4) は純粋関数 `nextActionAttention` /
  // `nextActionAttentionAriaLabel` (@domain/firm/next-action-attention) に集約する。
  // 営業アクション由来の `要確認` / `更新` だけを扱い、 メモ・契約・所属弁護士の更新は混ぜない。

  // 弁護士会列 (spec 20260612-multiple-firm-representative-lawyers)。
  // backend が「代表 1+ → 代表の bar 集合 / 代表 0 → firm の bar」 を導出済みなので、
  // ここでは短縮ラベル化して ", " 連結するだけ (例: 東京, 第二東京)。 空は -。
  function representativeBarAssociation(firm: Firm): string {
    const bars = firm.representative_bar_associations ?? [];
    if (bars.length === 0) return '-';
    return bars.map((bar) => formatBarAssociationLabel(bar)).join(', ');
  }

  // 利用ソリューションは複数契約をすべてチップ表示する (catalog No.9)。
  function productNames(firm: Firm): string[] {
    return (firm.active_products ?? []).map((p) => p.name);
  }

  function statusLabel(status: string): string {
    return CUSTOMER_STATUS_LABELS[status] ?? status;
  }

  function isBlank(value: string | null | undefined): boolean {
    return !value || value.trim().length === 0;
  }

  // 一括営業可否は backend の派生値 (事務所 5 フラグ由来) を描画するだけで、ここで
  // 合成しない。ラベルは仕様書「Header and List Labels」表 (由来表記なし)。
  function bulkSalesLabel(firm: Firm): string | null {
    return firmBulkSalesSummaryLabel(firm.bulk_sales_decision);
  }

  function hasExclusionFlag(firm: Firm): boolean {
    return bulkSalesLabel(firm) !== null || firm.is_competitor;
  }

  function isCompetitorReasonMissing(firm: Firm): boolean {
    return firm.is_competitor && isBlank(firm.competitor_reason);
  }
</script>

<div class="table-scroll">
  <table data-testid="firms-table">
    <colgroup>
      {#if canSelect}<col class="col-select-width" />{/if}
      <col class="col-firm-name-width" />
      <col class="col-lawyer-count-width" />
      <col class="col-bar-width" />
      <col class="col-status-width" />
      <col class="col-action-width" />
      <col class="col-date-width" />
      <col class="col-date-width" />
      <col class="col-products-width" />
      <col class="col-assignee-width" />
    </colgroup>
    <thead>
      <tr>
        {#if canSelect}
          <th class="select-cell">
            <input
              type="checkbox"
              checked={allSelected}
              indeterminate={someSelected}
              aria-label="表示中の法律事務所をすべて選択"
              disabled={selectionDisabled}
              onchange={(ev) =>
                onToggleAll((ev.currentTarget as HTMLInputElement).checked)}
            />
          </th>
        {/if}
        <th>
          <button
            type="button"
            class="sort"
            onclick={() => onSort('official_name')}
          >
            事務所名{indicator('official_name')}
          </button>
        </th>
        <th>
          <div class="th-cell">
            <FilterChip
              label="弁護士数"
              mode="multi"
              options={LAWYER_COUNT_BUCKET_OPTIONS}
              currentValue={query.lawyer_count_buckets ?? []}
              onApply={applyLawyerCountBuckets}
              variant="table-header"
            />
            <button
              type="button"
              class="sort-mini"
              class:active={sortBy === 'lawyer_count'}
              onclick={() => onSort('lawyer_count')}
              aria-label="弁護士数で並べ替え"
              title="並べ替え">{sortMark('lawyer_count')}</button
            >
          </div>
        </th>
        <th>
          <FilterChip
            label="弁護士会"
            mode="multi"
            options={BAR_ASSOCIATION_DISPLAY_OPTIONS}
            currentValue={query.bar_associations ?? []}
            onApply={applyBarAssociations}
            variant="table-header"
          />
        </th>
        <th>
          <div class="th-cell">
            <FilterChip
              label="ステータス"
              mode="multi"
              options={CUSTOMER_STATUS_OPTIONS}
              currentValue={statusChipValue}
              onApply={applyCustomerStatuses}
              variant="table-header"
            />
            <button
              type="button"
              class="sort-mini"
              class:active={sortBy === 'top_status'}
              onclick={() => onSort('top_status')}
              aria-label="ステータスで並べ替え"
              title="並べ替え">{sortMark('top_status')}</button
            >
          </div>
        </th>
        <th>
          <FilterChip
            label="次アクション"
            mode="multi"
            options={ACTION_TYPE_OPTIONS}
            currentValue={query.next_action_types ?? []}
            onApply={applyNextActionTypes}
            variant="table-header"
          />
        </th>
        <th class="th-right">
          <div class="th-cell">
            <FilterChip
              label="対応期限"
              mode="single"
              options={NEXT_ACTION_BUCKET_OPTIONS}
              currentValue={query.next_action_bucket ?? null}
              allLabel="指定なし"
              onApply={applyNextAction}
              variant="table-header"
            />
            <button
              type="button"
              class="sort-mini"
              class:active={sortBy === 'next_action_due_at'}
              onclick={() => onSort('next_action_due_at')}
              aria-label="対応期限で並べ替え"
              title="並べ替え">{sortMark('next_action_due_at')}</button
            >
          </div>
        </th>
        <th class="th-right">
          <div class="th-cell">
            <FilterChip
              label="最終接触"
              mode="single"
              options={LAST_CONTACT_BUCKET_OPTIONS}
              currentValue={query.last_contact_bucket ?? null}
              allLabel="指定なし"
              onApply={applyLastContact}
              variant="table-header"
            />
            <button
              type="button"
              class="sort-mini"
              class:active={sortBy === 'last_contact_at'}
              onclick={() => onSort('last_contact_at')}
              aria-label="最終接触で並べ替え"
              title="並べ替え">{sortMark('last_contact_at')}</button
            >
          </div>
        </th>
        <th class="th-right">
          <FilterChip
            label="利用ソリューション"
            mode="multi"
            options={productOptions}
            currentValue={query.product_ids ?? []}
            onApply={applyProductIds}
            variant="table-header"
          />
        </th>
        <th>
          <FilterChip
            label="担当者"
            mode="multi"
            options={accountOptions}
            currentValue={query.assignee_account_ids ?? []}
            onApply={applyAssignees}
            variant="table-header"
          />
        </th>
      </tr>
    </thead>
    <tbody>
      {#if firms.length === 0}
        <tr class="empty-row">
          <td colspan={colCount}>
            <div class="empty-state">
              <p class="empty">該当する法律事務所がありません</p>
              {#if hasActiveFilters}
                <button
                  type="button"
                  class="clear-filters"
                  onclick={onClearFilters}
                >
                  フィルタをクリア
                </button>
              {/if}
            </div>
          </td>
        </tr>
      {:else}
        {#each firms as firm (firm.id)}
          {@const attn = nextActionAttention(firm)}
          <tr
            class:exclusion-row={hasExclusionFlag(firm)}
            class:selected-row={canSelect && selectedIds.has(firm.id)}
            data-firm-id={firm.id}
          >
            {#if canSelect}
              <td class="select-cell">
                <input
                  type="checkbox"
                  checked={selectedIds.has(firm.id)}
                  aria-label="この法律事務所を選択"
                  disabled={selectionDisabled}
                  onchange={(ev) =>
                    onToggleRow(
                      firm.id,
                      (ev.currentTarget as HTMLInputElement).checked,
                    )}
                />
              </td>
            {/if}
            <td class="firm-name-cell">
              <div class="firm-name-content">
                <a href={`/firms/${firm.id}`} class="primary-link"
                  >{firm.official_name}</a
                >
                {#if hasExclusionFlag(firm)}
                  <div class="exclusion-badges" aria-label="営業対象外フラグ">
                    {#if bulkSalesLabel(firm)}
                      <span
                        class="exclusion-badge exclusion-badge-bulk-sales"
                        title={bulkSalesLabel(firm)}
                      >
                        {bulkSalesLabel(firm)}
                      </span>
                    {/if}
                    {#if firm.is_competitor}
                      <span
                        class="exclusion-badge exclusion-badge-competitor"
                        title={isCompetitorReasonMissing(firm)
                          ? '競合: 理由未設定'
                          : '競合'}
                      >
                        競合
                        {#if isCompetitorReasonMissing(firm)}
                          <span class="reason-missing" aria-label="理由未設定"
                            >!</span
                          >
                        {/if}
                      </span>
                    {/if}
                  </div>
                {/if}
              </div>
            </td>
            <td>{lawyerCountDisplay(firm)}</td>
            <td>{representativeBarAssociation(firm)}</td>
            <td>
              {#if firm.top_status}
                {statusLabel(firm.top_status)}
              {:else}
                <span class="muted">-</span>
              {/if}
            </td>
            <td class="col-next">
              <div class="next-body" title={firm.next_action ?? ''}>
                {nextActionLabel(firm.next_action)}
              </div>
              {#if attn.kind !== 'none'}
                <div
                  class="attention"
                  role="group"
                  aria-label={nextActionAttentionAriaLabel(firm)}
                >
                  {#if attn.kind === 'review'}
                    <span class="attn-badge attn-review" aria-hidden="true"
                      >要確認</span
                    >
                    {#if attn.showAuto}
                      <span class="attn-badge attn-auto" aria-hidden="true"
                        >自動</span
                      >
                    {/if}
                  {:else}
                    <span class="attn-badge attn-update" aria-hidden="true"
                      >更新</span
                    >
                  {/if}
                </div>
              {/if}
            </td>
            <td>{formatDate(firm.next_action_due_at)}</td>
            <td>{formatDate(firm.last_contact_at)}</td>
            <td class="col-products">
              {#if productNames(firm).length === 0}
                <span class="muted">-</span>
              {:else}
                <div class="product-chips">
                  {#each productNames(firm) as name, idx (idx)}
                    <span class="product-chip">{name}</span>
                  {/each}
                </div>
              {/if}
            </td>
            <td class="col-assignee">
              {#if firm.assignee_account_id}
                {assigneeLabel(firm)}
              {:else}
                <span class="muted">-</span>
              {/if}
            </td>
          </tr>
        {/each}
      {/if}
    </tbody>
  </table>
</div>

<style>
  /* 列ヘッダーにフィルタ (FilterChip) のポップオーバーを内包するため、 nested scroll を
     解除してページスクロールに委ねる。 overflow:auto のままだとポップオーバーが
     スクロール枠でクリップされるため visible にする。 */
  .table-scroll {
    max-width: 100%;
    overflow: visible;
  }

  table {
    width: 100%;
    table-layout: fixed;
  }

  .col-select-width {
    width: 36px;
  }
  .col-firm-name-width {
    width: 17%;
  }
  .col-lawyer-count-width {
    width: 104px;
  }
  .col-bar-width {
    width: 13%;
  }
  .col-status-width {
    width: 10%;
  }
  .col-action-width {
    width: 11%;
  }
  .col-date-width {
    width: 9.5%;
  }
  .col-products-width {
    width: 13%;
  }
  .col-assignee-width {
    width: 8%;
  }

  thead th {
    position: sticky;
    top: 0;
    z-index: 3;
    background: #f9fafb;
    box-shadow: 0 1px 0 var(--border, #e3e6eb);
  }

  th,
  td {
    text-align: center;
    vertical-align: middle;
    padding: 8px 7px;
    overflow-wrap: anywhere;
  }

  .select-cell {
    text-align: center;
    white-space: nowrap;
  }
  tbody tr.selected-row {
    background: #eff6ff;
  }
  tbody tr.selected-row.exclusion-row {
    background: #fff3f3;
  }

  tbody tr.exclusion-row {
    background: #fff8f8;
    box-shadow: inset 3px 0 0 #ef4444;
  }
  tbody tr:hover {
    background: #f8fafc;
  }
  tbody tr.exclusion-row:hover {
    background: #fff3f3;
  }

  /* 右端寄りのカラムと最終列 (担当者) のフィルタは .main (overflow-x:hidden) の
     右端でポップオーバーが切れるため、 右寄せで開く。 FilterChip 内部の .popover を
     scoped 経由で上書き。 */
  .th-right :global(.popover) {
    left: auto;
    right: 0;
  }
  th:last-child :global(.popover) {
    left: auto;
    right: 0;
  }

  th button.sort {
    background: transparent;
    border: 0;
    padding: 0;
    cursor: pointer;
    color: inherit;
    font: inherit;
    font-weight: 600;
  }
  th button.sort:hover {
    color: var(--primary, #2563eb);
  }

  .th-cell {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
    max-width: 100%;
  }
  .sort-mini {
    background: transparent;
    border: 0;
    padding: 0;
    cursor: pointer;
    color: var(--muted, #6b7280);
    font-size: 11px;
    line-height: 1;
  }
  .sort-mini:hover,
  .sort-mini.active {
    color: var(--primary, #2563eb);
  }

  .primary-link {
    color: var(--primary, #2563eb);
    font-weight: 500;
    text-decoration: none;
  }
  .primary-link:hover {
    text-decoration: underline;
  }

  .firm-name-cell {
    padding-top: 7px;
    padding-bottom: 7px;
    text-align: left;
  }
  .firm-name-content {
    display: inline-flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 3px;
    width: 100%;
    max-width: 100%;
  }
  .exclusion-badges {
    display: flex;
    flex-wrap: wrap;
    justify-content: flex-start;
    gap: 4px;
  }
  .exclusion-badge {
    display: inline-flex;
    align-items: center;
    gap: 3px;
    padding: 1px 6px;
    border-radius: 999px;
    font-size: 11px;
    font-weight: 600;
    line-height: 1.35;
    white-space: nowrap;
  }
  .exclusion-badge-bulk-sales {
    background: #fee2e2;
    color: #991b1b;
  }
  .exclusion-badge-competitor {
    background: #fff7ed;
    color: #9a3412;
  }
  .reason-missing {
    display: inline-grid;
    place-items: center;
    width: 13px;
    height: 13px;
    border-radius: 999px;
    background: rgba(185, 28, 28, 0.14);
    color: #991b1b;
    font-size: 10px;
    line-height: 1;
  }

  .muted {
    color: var(--muted, #6b7280);
  }

  .product-chips {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 4px;
  }
  .product-chip {
    display: inline-block;
    padding: 2px 8px;
    background: #eff6ff;
    color: #1d4ed8;
    border-radius: 999px;
    font-size: 12px;
    line-height: 1.35;
    max-width: 100%;
    overflow-wrap: anywhere;
  }

  .col-next .next-body {
    display: inline-block;
    font-size: 13px;
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    vertical-align: middle;
  }

  /* 営業アクション由来の注意表示 (Issue #524 PR4)。 件数バッジとは別概念のため
     中立色ではなく注意色を使う (要確認=アンバー / 更新=スカイ / 自動=インディゴ)。 */
  .col-next .attention {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 4px;
    margin-top: 3px;
  }
  .attn-badge {
    display: inline-block;
    padding: 1px 6px;
    border-radius: 999px;
    font-size: 11px;
    font-weight: 600;
    line-height: 1.35;
    white-space: nowrap;
  }
  .attn-review {
    background: #fef3c7;
    color: #92400e;
  }
  .attn-auto {
    background: #eef2ff;
    color: #3730a3;
  }
  .attn-update {
    background: #e0f2fe;
    color: #075985;
  }

  .col-assignee {
    line-height: 1.35;
  }

  .empty-row td {
    padding: 0;
  }
  .empty-state {
    display: flex;
    min-height: 128px;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 12px;
    padding: 28px;
  }
  .empty {
    margin: 0;
    text-align: center;
    color: var(--muted, #6b7280);
    font-size: 14px;
  }
  .clear-filters {
    border: 1px solid #d1d5db;
    border-radius: 999px;
    background: #ffffff;
    color: #374151;
    cursor: pointer;
    font: inherit;
    font-size: 13px;
    padding: 6px 12px;
  }
  .clear-filters:hover {
    border-color: var(--primary, #2563eb);
    color: var(--primary, #2563eb);
    background: #eff6ff;
  }
</style>
